import { useEffect, useRef } from "react";
import { gsap } from "./gsap";
import { prefersReducedMotion } from "./prefersReducedMotion";

interface ParallaxOptions {
  // How far (px) the element drifts across the full scrub range. Positive =
  // moves down slower than scroll (recedes); use small values for subtlety.
  amount?: number;
  deps?: unknown[];
}

/**
 * Smooth scrubbed parallax: the element drifts vertically as it passes through
 * the viewport, tied 1:1 to scroll position (scrub). Used on the hero media to
 * add depth as the page scrolls away. No-op under `prefers-reduced-motion`.
 */
export function useParallax<T extends HTMLElement = HTMLDivElement>(
  options: ParallaxOptions = {}
) {
  const ref = useRef<T>(null);
  const { amount = 120, deps = [] } = options;

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (prefersReducedMotion()) return;

    const ctx = gsap.context(() => {
      gsap.fromTo(
        el,
        { yPercent: 0 },
        {
          y: amount,
          ease: "none",
          scrollTrigger: {
            trigger: el,
            start: "top top",
            end: "bottom top",
            scrub: true,
          },
        }
      );
    }, el);

    return () => ctx.revert();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return ref;
}
